'use client';

import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { createClient } from '@/lib/supabase/client';

export type UserRole =
  | 'super_admin'
  | 'admin_lab'
  | 'biologo'
  | 'tecnico'
  | 'medico_validatore'
  | 'front_desk'
  | 'visualizzatore';

export interface Tenant {
  id: string;
  name: string;
  nome?: string;
  slug: string;
  plan: 'free' | 'pro' | 'enterprise';
  status: 'active' | 'suspended' | 'trial' | 'cancelled';
  owner_email: string;
  phone?: string;
  address?: string;
  city?: string;
  province?: string;
  logo_url?: string;
  codice_fiscale?: string;
  max_users: number;
  max_samples_per_month: number;
  trial_ends_at?: string;
  created_at: string;
}

export interface UserProfile {
  id: string;
  tenant_id: string | null;
  email: string;
  full_name: string;
  role: UserRole;
  title?: string;
  department?: string;
  phone?: string;
  avatar_url?: string;
  is_active: boolean;
  last_login_at?: string;
  created_at: string;
}

interface AuthContextType {
  user: any;
  session: any;
  profile: UserProfile | null;
  tenant: Tenant | null;
  loading: boolean;
  role: UserRole | null;
  isSuperAdmin: boolean;
  isAdminLab: boolean;
  canValidate: boolean;
  canAccept: boolean;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  hasRole: (roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  // Memoize supabase client — create once, never recreate
  const supabase = useMemo(() => createClient(), []);

  const fetchProfile = useCallback(
    async (userId: string) => {
      try {
        // Fetch profile and tenant in parallel
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select(
            'id, tenant_id, email, full_name, role, title, department, phone, avatar_url, is_active, last_login_at, created_at'
          )
          .eq('id', userId)
          .single();

        if (profileError || !profileData) return;

        setProfile(profileData as UserProfile);

        if (profileData.tenant_id) {
          const { data: tenantData } = await supabase
            .from('tenants')
            .select(
              'id, name, slug, plan, status, owner_email, phone, address, city, province, logo_url, max_users, max_samples_per_month, trial_ends_at, created_at'
            )
            .eq('id', profileData.tenant_id)
            .single();
          if (tenantData) setTenant(tenantData as Tenant);
        }
      } catch {
        // silently fail
      }
    },
    [supabase]
  );

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setProfile(null);
        setTenant(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const signUp = async (email: string, password: string, metadata: Record<string, any> = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Log login in audit
    if (data.user) {
      await supabase
        .from('audit_log')
        .insert({
          user_id: data.user.id,
          action: 'login',
          resource_type: 'auth',
          description: 'Login effettuato',
        })
        .then(({ error }) => {
          if (error) console.warn('Audit log login failed:', error.message);
        });
    }
    return data;
  };

  const signOut = async () => {
    if (user) {
      await supabase
        .from('audit_log')
        .insert({
          user_id: user.id,
          tenant_id: profile?.tenant_id ?? null,
          action: 'logout',
          resource_type: 'auth',
          description: 'Logout effettuato',
        })
        .then(({ error }) => {
          if (error) console.warn('Audit log logout failed:', error.message);
        });
    }
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setTenant(null);
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) throw new Error('Not authenticated');
    const { error } = await supabase.from('user_profiles').update(updates).eq('id', user.id);
    if (error) throw error;
    await refreshProfile();
  };

  const hasRole = (roles: UserRole[]): boolean => {
    if (!profile) return false;
    return roles.includes(profile.role);
  };

  const role = profile?.role ?? null;
  const isSuperAdmin = role === 'super_admin';
  const isAdminLab = role === 'admin_lab' || isSuperAdmin;
  const canValidate = ['biologo', 'medico_validatore', 'admin_lab', 'super_admin'].includes(
    role ?? ''
  );
  const canAccept = ['front_desk', 'tecnico', 'admin_lab', 'super_admin'].includes(role ?? '');

  return (
    <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:226:4" data-component-path="src/contexts/AuthContext.tsx" data-component-line="226" data-component-end-line="247" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%7D"
      value={{
        user,
        session,
        profile,
        tenant,
        loading,
        role,
        isSuperAdmin,
        isAdminLab,
        canValidate,
        canAccept,
        signUp,
        signIn,
        signOut,
        refreshProfile,
        updateProfile,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
